import {
  clearReviewResourceCompletions,
  watchReviewCompletion,
} from "@/entities/notification/completion-alerts"
import { clearCompletionInboxResource } from "@/entities/notification/completion-inbox"
import { CustomUseMutationOptions } from "@/shared/lib/react-query/react-query.lib"
import { waitForAuthenticationRefresh } from "@/shared/lib/axios/axios.interceptors"
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query"
import { useEffect, useMemo, useRef, useState } from "react"
import {
  CATReviewQuotaError,
  CATReviewTooLargeError,
  getLatestCatReviewRun,
  startCatReviewRun,
} from "./cat-review.api"
import type { CATReviewRun } from "./cat-review.types"
import { stripInlineTags } from "./cat-inline-tags.lib"
import {
  applyCATInvitationAnswer,
  hasActionableCATAssignments,
} from "@/features/mt/cat-invitation.lib"
import { useDocumentJobStore, type DocumentJobInfo } from "./translation.model"
import {
  bulkEditSegments,
  addCatSegmentComment,
  createCatQARun,
  createGlossary,
  deleteFile,
  deleteGlossary,
  editMappingsOfGlossary,
  exportCatXliff,
  getDocumentTranslationProgress,
  getAssemblyStatus,
  getAssignedCatWorkspaces,
  getCatSegmentQE,
  getCatSegmentComments,
  getCatSegmentEditHistory,
  getLatestCatQARun,
  getCatWorkspace,
  completeCatWorkflow,
  deleteCatWorkflowAssignment,
  getDocumentJob,
  getDocumentVersions,
  requestDocumentAssembly,
  reopenCatWorkflow,
  respondToCatWorkflowInvitation,
  runCatQA,
  setCatQAFeedback,
  uploadDocumentRevision,
  getGlossaryDetail,
  getGlossaryList,
  getSmartGlossaries,
  mergeSmartGlossaryIntoExistingGlossary,
  predictCost,
  sendFeedback,
  startMediaTranslation,
  translateFile,
  translateText,
  updateEditorTitle,
  updateCatSegmentComment,
  updateCatSegmentReview,
  upsertCatWorkflowAssignment,
  updateSmartGlossary,
} from "./translation.api"
import { TranslationQueryKeysEnum } from "./translation.constants"
import {
  AssemblyStatusResponse,
  CATAssignedWorkspacePage,
  CATInvitationState,
  DocumentTranslationProgress,
  DocumentVersionInfo,
  CATSegmentQEResponse,
  CATQAResponse,
  CATQARunResponse,
  CATSegmentCommentsResponse,
  CATSegmentEditHistoryResponse,
  CATWorkspaceResponse,
  FileQueue,
  Glossary,
  SmartGlossary,
  TranslationEngineEnum,
  TranslationPhaseEnum,
} from "./translation.types"

export interface LineageInfo {
  version: number
  lineageId: string
  parentJobId: string | null
  engine?: TranslationEngineEnum
}

export const getFileQueueLineageInfo = (
  file: FileQueue
): LineageInfo | null => {
  if (
    file.version === undefined ||
    file.lineageId === undefined ||
    file.parentJobId === undefined
  ) {
    return null
  }

  return {
    version: file.version,
    lineageId: file.lineageId,
    parentJobId: file.parentJobId,
    engine: file.engine ?? TranslationEngineEnum.BeringAI,
  }
}

export const useDocumentJobQuery = ({
  jobId,
  enabled = true,
}: {
  jobId: string
  enabled?: boolean
}) => {
  const queryClient = useQueryClient()

  return useQuery<DocumentJobInfo>({
    queryKey: [
      TranslationQueryKeysEnum.FetchSmartGlossaries,
      "document-job",
      jobId,
    ],
    queryFn: async () => {
      // EventSourcePolyfill bypasses the Axios interceptor that refreshes an
      // expired access token. Run the versions request through Axios first;
      // QueryClient de-duplicates it with useDocumentVersionsQuery below.
      await queryClient
        .fetchQuery({
          queryKey: [TranslationQueryKeysEnum.FetchDocumentVersions, jobId],
          queryFn: () => getDocumentVersions({ jobId }),
          staleTime: 0,
        })
        // The REST call is an authentication-refresh opportunity, not an
        // availability gate. Standalone jobs or transient versions failures
        // can still have a usable metadata stream.
        .catch(() => waitForAuthenticationRefresh())

      return getDocumentJob({ jobId })
    },
    enabled: !!jobId && enabled,
    staleTime: Infinity,
  })
}

/**
 * Returns file metadata for a given job by merging two sources:
 *
 * - Zustand store (persisted to sessionStorage) → navigation metadata
 * - document-job API → complete metadata on direct entry or fresh sessions
 * - `/versions` API (REST) → language/domain fallback
 *
 * When Zustand matches the jobId, its data is used directly (covers
 * navigation and refresh). Otherwise the job endpoint supplies the file
 * path and title needed for editor downloads, with `/versions` as a safe
 * partial fallback while that request is unavailable.
 */
export const useDocumentJobInfo = ({ jobId }: { jobId: string }) => {
  const storeJob = useDocumentJobStore((s) => s.selectedJob)
  const storeMatchesJob = storeJob?.jobId === jobId
  const { data: fetchedJob, isLoading: isJobLoading } = useDocumentJobQuery({
    jobId,
    enabled: !storeMatchesJob,
  })
  const { data: versions, isLoading: isVersionsLoading } =
    useDocumentVersionsQuery({ jobId })

  const fileInfo: DocumentJobInfo | null = useMemo(() => {
    const storeMatch = storeMatchesJob ? storeJob : null
    const version = versions?.find((candidate) => candidate.id === jobId)

    // Zustand has full data for this job — use it directly.
    if (storeMatch) return storeMatch

    // Direct editor URLs and fresh sessions have no selectedJob. Fetch the
    // complete job metadata so DOCX downloadability does not depend on
    // navigation state.
    if (fetchedJob) {
      return {
        jobId: fetchedJob.jobId,
        sourceTitle: fetchedJob.sourceTitle,
        sourceLanguage: fetchedJob.sourceLanguage,
        targetLanguage: fetchedJob.targetLanguage,
        domain: fetchedJob.domain,
        inputPath: fetchedJob.inputPath,
        engine: fetchedJob.engine ?? version?.engine,
      }
    }

    // Zustand doesn't match (different job or cleared). Fall back to
    // /versions for language/domain/engine; sourceTitle is unavailable.
    if (version) {
      return {
        jobId,
        sourceLanguage: version.sourceLanguage,
        targetLanguage: version.targetLanguage,
        domain: version.domain,
        engine: version.engine,
        sourceTitle: "",
        inputPath: "",
      }
    }

    return null
  }, [versions, storeJob, storeMatchesJob, fetchedJob, jobId])

  return {
    fileInfo,
    isLoading: !fileInfo && (isJobLoading || isVersionsLoading),
  }
}

export const useGlossaryListQuery = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  return useQuery<Glossary[]>({
    queryKey: [TranslationQueryKeysEnum.FetchGlossaryList],
    queryFn: getGlossaryList,
    enabled,
  })
}

export const useGlossaryDetailQuery = ({
  glossaryId,
  enabled,
}: {
  glossaryId: string
  enabled: boolean
}) => {
  return useQuery<Glossary>({
    queryKey: [TranslationQueryKeysEnum.FetchGlossaryDetail, glossaryId],
    queryFn: () => getGlossaryDetail({ glossaryId }),
    enabled,
  })
}

export const useGetSmartGlossariesQuery = ({
  jobId,
  enabled,
}: {
  jobId: string
  enabled: boolean
}) => {
  return useQuery<SmartGlossary>({
    queryKey: [TranslationQueryKeysEnum.FetchSmartGlossaries, jobId],
    queryFn: () => getSmartGlossaries({ jobId }),
    enabled,
    staleTime: Infinity,
  })
}

export const useDocumentTranslationProgressQuery = ({
  jobId,
  enabled = true,
}: {
  jobId: string
  enabled?: boolean
}) => {
  return useQuery<DocumentTranslationProgress>({
    queryKey: [
      TranslationQueryKeysEnum.FetchDocumentTranslationProgress,
      jobId,
    ],
    queryFn: () => getDocumentTranslationProgress({ jobId }),
    enabled: !!jobId && enabled,
    refetchInterval: (query) => {
      const phase = query.state.data?.phase
      if (
        phase === TranslationPhaseEnum.Completed ||
        phase === TranslationPhaseEnum.Failed
      ) {
        return false
      }
      return 4000
    },
  })
}

export const useCatWorkspaceQuery = ({
  jobId,
  compareWithPrevious = false,
  compareWith,
  enabled = true,
  refetchInterval = false,
}: {
  jobId: string
  compareWithPrevious?: boolean
  compareWith?: string
  enabled?: boolean
  refetchInterval?: number | false
}) => {
  return useQuery<CATWorkspaceResponse>({
    queryKey: [
      TranslationQueryKeysEnum.FetchCatWorkspace,
      jobId,
      compareWithPrevious,
      compareWith ?? null,
    ],
    queryFn: () => getCatWorkspace({ jobId, compareWithPrevious, compareWith }),
    enabled: !!jobId && enabled,
    staleTime: Infinity,
    refetchInterval,
  })
}

const ASSIGNED_CAT_WORKSPACES_PAGE_SIZE = 20
const ASSIGNED_CAT_WORKSPACES_NAV_REFRESH_INTERVAL = 60_000

/**
 * 내비게이션 판정에 쓰는 표본 크기.
 *
 * 한 행으로는 "거절한 행 하나"와 "맡은 일 하나"를 구분할 수 없다. 다 못 보면 켜는 쪽으로 기울므로
 * 크게 잡을수록 정확해질 뿐 안전 방향은 바뀌지 않는다.
 */
const ASSIGNED_CAT_WORKSPACES_NAV_PROBE_SIZE = 20

export const useHasAssignedCatWorkspacesQuery = ({
  enabled = true,
}: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: [
      TranslationQueryKeysEnum.FetchAssignedCatWorkspaces,
      "navigation",
    ],
    queryFn: () =>
      getAssignedCatWorkspaces({
        page: 0,
        itemsPerPage: ASSIGNED_CAT_WORKSPACES_NAV_PROBE_SIZE,
      }),
    // 한 행만 가져와서는 거절한 행과 맡은 일을 구분할 수 없다. 판정은 행 상태로 한다.
    select: hasActionableCATAssignments,
    enabled,
    staleTime: 60_000,
    refetchInterval: ASSIGNED_CAT_WORKSPACES_NAV_REFRESH_INTERVAL,
  })

export const useAssignedCatWorkspacesQuery = () =>
  useInfiniteQuery<CATAssignedWorkspacePage>({
    queryKey: [TranslationQueryKeysEnum.FetchAssignedCatWorkspaces],
    queryFn: ({ pageParam }) =>
      getAssignedCatWorkspaces({
        page: pageParam as number,
        itemsPerPage: ASSIGNED_CAT_WORKSPACES_PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((count, page) => count + page.items.length, 0)
      return loaded < lastPage.total ? pages.length : undefined
    },
  })

export const useDocumentVersionsQuery = ({
  jobId,
  enabled = true,
}: {
  jobId: string
  enabled?: boolean
}) => {
  return useQuery<DocumentVersionInfo[]>({
    queryKey: [TranslationQueryKeysEnum.FetchDocumentVersions, jobId],
    queryFn: () => getDocumentVersions({ jobId }),
    enabled: !!jobId && enabled,
    staleTime: 30_000,
  })
}

/**
 * Builds lineage metadata directly from the file-list SSE payload. During a
 * mixed-version rollout, rows from an older backend may not carry lineage
 * fields yet; only those legacy rows fall back to `/documents/{id}/versions`.
 *
 * The fallback response contains the full lineage, so sibling job ids are
 * marked together and fetched at most once per lineage.
 */
export const useFileQueueLineageMap = (
  fileQueue: FileQueue[]
): Map<string, LineageInfo> => {
  const queryClient = useQueryClient()
  const processedRef = useRef<Set<string>>(new Set())
  const [fallbackMap, setFallbackMap] = useState<Map<string, LineageInfo>>(
    new Map()
  )

  const streamMap = useMemo(() => {
    const next = new Map<string, LineageInfo>()
    for (const file of fileQueue) {
      const info = getFileQueueLineageInfo(file)
      if (info) next.set(file.jobId, info)
    }
    return next
  }, [fileQueue])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      for (const f of fileQueue) {
        if (cancelled) return
        if (getFileQueueLineageInfo(f)) continue
        if (processedRef.current.has(f.jobId)) continue
        try {
          const versions = await queryClient.fetchQuery<DocumentVersionInfo[]>({
            queryKey: [TranslationQueryKeysEnum.FetchDocumentVersions, f.jobId],
            queryFn: () => getDocumentVersions({ jobId: f.jobId }),
            staleTime: 60_000,
          })
          if (cancelled) return
          const updates: Array<[string, LineageInfo]> = []
          for (const v of versions) {
            processedRef.current.add(v.id)
            updates.push([
              v.id,
              {
                version: v.version,
                lineageId: v.lineageId,
                parentJobId: v.parentJobId,
                engine: v.engine,
              },
            ])
          }
          setFallbackMap((prev) => {
            const next = new Map(prev)
            for (const [k, v] of updates) next.set(k, v)
            return next
          })
        } catch {
          // 404 / network — treat as standalone v1 so we don't refetch
          // forever; the row will just appear ungrouped.
          processedRef.current.add(f.jobId)
          setFallbackMap((prev) => {
            const next = new Map(prev)
            next.set(f.jobId, {
              version: 1,
              lineageId: f.jobId,
              parentJobId: null,
            })
            return next
          })
        }
      }
    }
    if (fileQueue.length > 0) run()
    return () => {
      cancelled = true
    }
  }, [fileQueue, queryClient])

  return useMemo(
    () => new Map([...fallbackMap, ...streamMap]),
    [fallbackMap, streamMap]
  )
}

export const useCatSegmentQEQuery = ({
  jobId,
  enabled = true,
}: {
  jobId: string
  enabled?: boolean
}) => {
  return useQuery<CATSegmentQEResponse>({
    queryKey: [TranslationQueryKeysEnum.FetchCatSegmentQE, jobId],
    queryFn: () => getCatSegmentQE({ jobId }),
    enabled: !!jobId && enabled,
    staleTime: Infinity,
    // QE is computed once per job version; retrying network failures is fine
    // but 4xx (bad request / not found) should not retry — they mean QE is
    // permanently unavailable for this job.
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status
      if (status && status >= 400 && status < 500) return false
      return failureCount < 2
    },
  })
}

export const useLatestCatQARunQuery = ({
  jobId,
  enabled = true,
}: {
  jobId: string
  enabled?: boolean
}) =>
  useQuery<CATQARunResponse>({
    queryKey: [TranslationQueryKeysEnum.FetchCatQALatestRun, jobId],
    queryFn: () => getLatestCatQARun({ jobId }),
    enabled: !!jobId && enabled,
    staleTime: 30_000,
    refetchInterval: enabled ? 30_000 : false,
  })

/**
 * Follows a review run to its end.
 *
 * Polls only while there is something to wait for, and stops on a terminal status. There is no
 * client-side attempt cap on purpose: the imperative poll loop used for XLIFF export gives up
 * after a fixed number of tries, which is a deadline nobody chose — the same mistake as inheriting
 * a proxy's 60 seconds — and it cannot resume after the component unmounts. The server owns the
 * deadline here and expresses it as a terminal status.
 */
export const useCatReviewLatestRunQuery = ({
  jobId,
  enabled = true,
}: {
  jobId: string
  enabled?: boolean
}) => {
  const query = useQuery<CATReviewRun | null>({
    queryKey: [TranslationQueryKeysEnum.FetchCatReviewLatestRun, jobId],
    queryFn: () => getLatestCatReviewRun({ jobId }),
    // Gated on access, so opening the editor as a non-staff translator does not poll an endpoint
    // the server will refuse.
    enabled: !!jobId && enabled,
    // A single failed poll must not strand the panel in a spinner for the rest of the run.
    retry: 3,
  })

  const { refetch, data } = query
  useEffect(() => {
    if (
      enabled &&
      data &&
      (data.status === "queued" || data.status === "running")
    ) {
      watchReviewCompletion({
        kind: "cat-review",
        resourceId: jobId,
        runId: data.id,
        startedAt: data.createdAt,
      })
    }
  }, [enabled, jobId, data])
  // `enabled` is part of the condition, not just the query's. Cached data outlives a gate closing
  // — access being revoked, or the job changing — and an effect that only looked at the data would
  // go on polling an endpoint the server refuses, past the check that exists to stop it.
  const isPollable = !!jobId && enabled
  const isRunActive =
    isPollable && (data?.status === "queued" || data?.status === "running")

  // The interval is driven here rather than by `refetchInterval`.
  //
  // Measured on dev: the option form is evaluated as `false` while there is no run, and starting
  // one seeds the cache through `setQueryData` — after which the interval never begins. The run
  // completed on the server and the panel sat on "queued" forever; only a reload showed the
  // result. Polling that stops the moment it is needed is worse than not polling at all, because
  // the button looks like it did nothing.
  //
  // An effect keyed on whether a run is live has no such subtlety: the seed re-renders, the effect
  // sees an active run, and the interval starts.
  useEffect(() => {
    if (!isRunActive) return
    // A run takes tens of seconds to minutes, not the couple of seconds an assembly takes.
    //
    // `cancelRefetch: false` because an imperative refetch defaults to cancelling whatever is
    // already in flight. On a slow connection — or during one of the configured retries — every
    // tick would kill the request the previous tick started, nothing would ever come back, and
    // the panel would sit on the seeded "queued" exactly as it did before this fix.
    const timer = window.setInterval(() => {
      // Skipped while the tab is hidden, which is what the built-in interval did by default.
      // Nothing is lost: the interval keeps ticking, so a run that finished out of sight is
      // picked up within one tick of coming back.
      if (document.hidden) return
      void refetch({ cancelRefetch: false })
    }, 3_000)
    return () => window.clearInterval(timer)
  }, [isRunActive, refetch])

  return query
}

export const useStartCatReviewRunMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: startCatReviewRun,
    // The first latest-run GET may still be in flight when Run is pressed. If it resolves after
    // the POST it overwrites the seeded run with null, which stops the polling interval — the run
    // is live on the server and the button looks idle. Cancelling it first is the fix; seeding
    // without cancelling works right up until the two happen to race.
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: [
          TranslationQueryKeysEnum.FetchCatReviewLatestRun,
          variables.jobId,
        ],
      })
    },
    onSuccess: (run, variables) => {
      if (run && (run.status === "queued" || run.status === "running")) {
        watchReviewCompletion({
          kind: "cat-review",
          resourceId: variables.jobId,
          runId: run.id,
          startedAt: run.createdAt,
        })
      }
      // Seeded so the panel flips to "queued" immediately rather than looking idle until the first
      // poll lands, which would invite a second press.
      queryClient.setQueryData(
        [TranslationQueryKeysEnum.FetchCatReviewLatestRun, variables.jobId],
        run
      )
    },
    onError: (error, variables) => {
      // A failed POST does not mean no run. If the server created one and the response was lost to
      // a timeout or a dropped connection, nothing seeds the cache, the interval stays off, and
      // the panel reports a failed start while a paid run carries on without being followed.
      //
      // The two failures that are definitive are exempt: too-large and out-of-allowance are both
      // refused before a row exists, so there is nothing to go and look for.
      if (
        error instanceof CATReviewTooLargeError ||
        error instanceof CATReviewQuotaError
      ) {
        return
      }
      void queryClient.invalidateQueries({
        queryKey: [
          TranslationQueryKeysEnum.FetchCatReviewLatestRun,
          variables.jobId,
        ],
      })
    },
  })
}

export const useCatSegmentCommentsQuery = ({
  jobId,
  segmentId,
  enabled = true,
}: {
  jobId: string
  segmentId?: string
  enabled?: boolean
}) =>
  useQuery<CATSegmentCommentsResponse>({
    queryKey: [
      TranslationQueryKeysEnum.FetchCatSegmentComments,
      jobId,
      segmentId,
    ],
    queryFn: () => getCatSegmentComments({ jobId, segmentId: segmentId! }),
    enabled: !!jobId && !!segmentId && enabled,
  })

export const useCatSegmentEditHistoryQuery = ({
  jobId,
  segmentId,
  enabled = true,
}: {
  jobId: string
  segmentId?: string
  enabled?: boolean
}) =>
  useInfiniteQuery<CATSegmentEditHistoryResponse>({
    queryKey: [
      TranslationQueryKeysEnum.FetchCatSegmentEditHistory,
      jobId,
      segmentId,
    ],
    queryFn: ({ pageParam }) =>
      getCatSegmentEditHistory({
        jobId,
        segmentId: segmentId!,
        beforeRevision: pageParam as number | undefined,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextBeforeRevision ?? undefined,
    enabled: !!jobId && !!segmentId && enabled,
  })

export const useAssemblyStatusQuery = ({
  jobId,
  assemblyRequestId,
  enabled = true,
}: {
  jobId: string
  assemblyRequestId: string | null
  enabled?: boolean
}) => {
  return useQuery<AssemblyStatusResponse>({
    queryKey: [
      TranslationQueryKeysEnum.FetchAssemblyStatus,
      jobId,
      assemblyRequestId,
    ],
    queryFn: () =>
      getAssemblyStatus({ jobId, assemblyRequestId: assemblyRequestId! }),
    enabled: !!jobId && !!assemblyRequestId && enabled,
    // Poll while assembly is running; stop on completed/failed.
    refetchInterval: (query) => {
      const state = query.state.data?.status?.state
      if (state === "completed" || state === "failed") return false
      return 2000
    },
    // A status poll may fail transiently, but the editor must eventually
    // release its downloading state if the backend stays unavailable.
    retry: 3,
  })
}

// mutations
export const useRequestDocumentAssemblyMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: requestDocumentAssembly,
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data)
    },
    onError,
    onSettled,
  })
}

export const useExportCatXliffMutation = () => {
  return useMutation({ mutationFn: exportCatXliff })
}

export const useRunCatQAMutation = () => {
  return useMutation({ mutationFn: runCatQA })
}

export const useCreateCatQARunMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createCatQARun,
    // A latest-run GET can finish after the POST and overwrite the newly-created run in cache.
    // Cancel every older request before creating the authoritative replacement.
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: [
          TranslationQueryKeysEnum.FetchCatQALatestRun,
          variables.jobId,
        ],
      })
    },
    onSuccess: async (data, variables) => {
      // Polling can start another GET while the POST is running. Cancel that second wave too,
      // immediately before seeding the newly-created authoritative run.
      await queryClient.cancelQueries({
        queryKey: [
          TranslationQueryKeysEnum.FetchCatQALatestRun,
          variables.jobId,
        ],
      })
      queryClient.setQueryData(
        [TranslationQueryKeysEnum.FetchCatQALatestRun, variables.jobId],
        data
      )
    },
  })
}

export const useCatQAFeedbackMutation = ({
  onSuccess,
  onRefreshFailed,
}: {
  onSuccess?: (
    report: CATQAResponse,
    variables: Parameters<typeof setCatQAFeedback>[0]
  ) => void
  onRefreshFailed?: () => void
} = {}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: setCatQAFeedback,
    onSuccess: async (data, variables) => {
      const queryKey = [
        TranslationQueryKeysEnum.FetchCatQALatestRun,
        variables.jobId,
      ]
      const startedDataUpdatedAt =
        queryClient.getQueryState(queryKey)?.dataUpdatedAt ?? 0

      // Let the editor mark this exact refresh before invalidation can publish a stale run.
      onSuccess?.(data, variables)
      await queryClient.invalidateQueries({ queryKey })

      // A failed query keeps its previous data timestamp. Retry once while the mutation still
      // blocks other QA/resource actions, then expire the marker rather than letting an unrelated
      // later invalidation consume it.
      if (
        queryClient.getQueryState(queryKey)?.dataUpdatedAt ===
        startedDataUpdatedAt
      ) {
        await queryClient.refetchQueries({ queryKey })
      }
      if (
        queryClient.getQueryState(queryKey)?.dataUpdatedAt ===
        startedDataUpdatedAt
      ) {
        onRefreshFailed?.()
      }
    },
  })
}

export const useCatSegmentReviewMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateCatSegmentReview,
    onSuccess: (data, variables) => {
      queryClient.setQueriesData<CATWorkspaceResponse>(
        {
          queryKey: [
            TranslationQueryKeysEnum.FetchCatWorkspace,
            variables.jobId,
          ],
        },
        (old) => {
          if (!old?.data) return old
          const entry = Object.entries(old.data.segments).find(
            ([, segment]) => segment.id === variables.segmentId
          )
          if (!entry) return old
          const [key, segment] = entry
          return {
            ...old,
            data: {
              ...old.data,
              reviewProgress: data.reviewProgress,
              segments: {
                ...old.data.segments,
                [key]: { ...segment, review: data.review },
              },
            },
          }
        }
      )
    },
  })
}

const patchCatWorkflowCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  jobId: string,
  workflow: NonNullable<CATWorkspaceResponse["data"]>["workflow"]
) => {
  queryClient.setQueriesData<CATWorkspaceResponse>(
    {
      queryKey: [TranslationQueryKeysEnum.FetchCatWorkspace, jobId],
    },
    (old) => (old?.data ? { ...old, data: { ...old.data, workflow } } : old)
  )
}

export const useUpsertCatWorkflowAssignmentMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: upsertCatWorkflowAssignment,
    onSuccess: (data, variables) =>
      patchCatWorkflowCache(queryClient, variables.jobId, data.workflow),
  })
}

export const useDeleteCatWorkflowAssignmentMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteCatWorkflowAssignment,
    onSuccess: (data, variables) =>
      patchCatWorkflowCache(queryClient, variables.jobId, data.workflow),
  })
}

export const useCompleteCatWorkflowMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: completeCatWorkflow,
    onSuccess: (data, variables) => {
      patchCatWorkflowCache(queryClient, variables.jobId, data.workflow)
      void queryClient.invalidateQueries({
        queryKey: [TranslationQueryKeysEnum.FetchAssignedCatWorkspaces],
      })
    },
  })
}

export const useReopenCatWorkflowMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: reopenCatWorkflow,
    onSuccess: (data, variables) => {
      patchCatWorkflowCache(queryClient, variables.jobId, data.workflow)
      void queryClient.invalidateQueries({
        queryKey: [TranslationQueryKeysEnum.FetchAssignedCatWorkspaces],
      })
    },
  })
}

/**
 * 자기 앞으로 온 초대에 답한다.
 *
 * 응답이 워크플로 전체가 아니라 초대 한 건이라 캐시를 덧칠할 수 없다.
 *
 * 목록은 실패해도 무효화한다. 404는 그 사이에 초대가 사라졌다는 뜻이라 목록이 이미 틀렸고, 그대로
 * 두면 카드가 답할 수 없는 초대를 계속 보여준다.
 *
 * 워크스페이스 캐시는 `invalidateQueries`가 아니라 `removeQueries`로 버린다. 무효화는 stale 표시일
 * 뿐 데이터를 지우지 않고, 비활성 쿼리는 다시 읽지도 않는다(기본 `refetchType: "active"`). 편집기는
 * `isError`를 보지 않으므로 캐시가 남아 있으면 권한이 사라진 뒤에도 세그먼트를 그대로 그린다.
 * 지금은 pending 인 사람만 거절 버튼을 보고 pending 은 애초에 캐시가 없어 도달하지 않지만, 주석이
 * 약속하는 동작과 코드가 갈라져 있으면 다음 사람이 그 약속을 믿는다.
 *
 * 무효화 프라미스를 반환한다. `void`로 버리면 POST가 끝나는 순간 `isPending`이 내려가 목록이
 * 갱신되기 전에 두 버튼이 다시 살아나고, 방금 답한 카드를 한 번 더 누를 수 있다.
 *
 * 성공하면 먼저 캐시의 해당 행을 응답으로 덧칠한다. 재조회가 실패하면 무효화는 이전 데이터를
 * 그대로 두므로 카드가 `pending`으로 남고 버튼이 다시 살아나는데, POST 자체는 성공했으니 오류
 * 토스트도 뜨지 않는다 — 답을 이미 보낸 사람이 답하지 않은 화면을 보게 된다.
 */
const patchCatInvitationCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  jobId: string,
  invitationState: CATInvitationState
) => {
  queryClient.setQueriesData<InfiniteData<CATAssignedWorkspacePage>>(
    { queryKey: [TranslationQueryKeysEnum.FetchAssignedCatWorkspaces] },
    (old) =>
      old?.pages
        ? {
            ...old,
            pages: applyCATInvitationAnswer(old.pages, jobId, invitationState),
          }
        : old
  )
}

export const useRespondToCatWorkflowInvitationMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: respondToCatWorkflowInvitation,
    onSuccess: (data) =>
      patchCatInvitationCache(
        queryClient,
        data.invitation.jobId,
        data.invitation.invitationState
      ),
    onSettled: (_data, _error, variables) => {
      queryClient.removeQueries({
        queryKey: [TranslationQueryKeysEnum.FetchCatWorkspace, variables.jobId],
      })
      return queryClient.invalidateQueries({
        queryKey: [TranslationQueryKeysEnum.FetchAssignedCatWorkspaces],
      })
    },
  })
}

export const useAddCatSegmentCommentMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: addCatSegmentComment,
    onSuccess: (data, variables) => {
      queryClient.setQueriesData<CATWorkspaceResponse>(
        {
          queryKey: [
            TranslationQueryKeysEnum.FetchCatWorkspace,
            variables.jobId,
          ],
        },
        (old) => {
          if (!old?.data) return old
          const entry = Object.entries(old.data.segments).find(
            ([, segment]) => segment.id === variables.segmentId
          )
          if (!entry) return old
          const [key, segment] = entry
          return {
            ...old,
            data: {
              ...old.data,
              segments: {
                ...old.data.segments,
                [key]: {
                  ...segment,
                  review: {
                    status:
                      segment.review?.status ??
                      (segment.translatedText !== null
                        ? "translated"
                        : "untranslated"),
                    reviewerId: segment.review?.reviewerId ?? null,
                    reviewerName: segment.review?.reviewerName ?? null,
                    commentCount: (segment.review?.commentCount ?? 0) + 1,
                    unresolvedCommentCount:
                      (segment.review?.unresolvedCommentCount ?? 0) +
                      (variables.parentCommentId ? 0 : 1),
                    locked: segment.review?.locked ?? false,
                    lockedBy: segment.review?.lockedBy ?? null,
                    lockedByName: segment.review?.lockedByName ?? null,
                  },
                },
              },
            },
          }
        }
      )
      queryClient.setQueryData<CATSegmentCommentsResponse>(
        [
          TranslationQueryKeysEnum.FetchCatSegmentComments,
          variables.jobId,
          variables.segmentId,
        ],
        (old) =>
          old
            ? {
                ...old,
                comments: old.comments.some(
                  (comment) => comment.id === data.comment.id
                )
                  ? old.comments
                  : [
                      ...old.comments.map((comment) =>
                        comment.id === variables.parentCommentId
                          ? {
                              ...comment,
                              resolvedAt: null,
                              resolvedBy: null,
                            }
                          : comment
                      ),
                      data.comment,
                    ],
              }
            : old
      )
      void queryClient.invalidateQueries({
        queryKey: [
          TranslationQueryKeysEnum.FetchCatSegmentComments,
          variables.jobId,
          variables.segmentId,
        ],
      })
      void queryClient.invalidateQueries({
        queryKey: [TranslationQueryKeysEnum.FetchCatWorkspace, variables.jobId],
      })
    },
  })
}

export const useUpdateCatSegmentCommentMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateCatSegmentComment,
    onSuccess: (data, variables) => {
      queryClient.setQueryData<CATSegmentCommentsResponse>(
        [
          TranslationQueryKeysEnum.FetchCatSegmentComments,
          variables.jobId,
          variables.segmentId,
        ],
        (old) =>
          old
            ? {
                ...old,
                comments: old.comments.map((comment) =>
                  comment.id === data.comment.id ? data.comment : comment
                ),
              }
            : old
      )
      void queryClient.invalidateQueries({
        queryKey: [TranslationQueryKeysEnum.FetchCatWorkspace, variables.jobId],
      })
    },
  })
}

export const useUploadDocumentRevisionMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: uploadDocumentRevision,
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data)
    },
    onError,
    onSettled,
  })
}

export const useBulkEditSegmentsMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: bulkEditSegments,
    onSuccess: (data, variables) => {
      // Patch cache before invalidate so the pre-edit data never paints on remount.
      queryClient.setQueriesData<CATWorkspaceResponse>(
        {
          queryKey: [
            TranslationQueryKeysEnum.FetchCatWorkspace,
            variables.jobId,
          ],
        },
        (old) => {
          if (!old?.data) return old
          const nextSegments = { ...old.data.segments }
          // Record keys are not the same as CATSegment.id (see editor's parsing of [key, seg] entries), so resolve id → record key once.
          const keyById = new Map<string, string>()
          for (const [key, seg] of Object.entries(nextSegments)) {
            keyById.set(seg.id, key)
          }
          let changed = false
          for (const edit of variables.edits) {
            const recordKey = keyById.get(edit.segmentId)
            if (recordKey === undefined) continue
            const seg = nextSegments[recordKey]
            const translatedText =
              edit.protectedInlineTags !== undefined
                ? stripInlineTags(edit.newTranslatedText)
                : edit.newTranslatedText
            const taggedTranslatedText =
              edit.protectedInlineTags !== undefined
                ? edit.newTranslatedText
                : seg.taggedTranslatedText
            if (
              seg.translatedText !== translatedText ||
              seg.taggedTranslatedText !== taggedTranslatedText ||
              (data.segmentRevisions?.[edit.segmentId] !== undefined &&
                seg.revision !== data.segmentRevisions[edit.segmentId]) ||
              seg.review?.status !== "translated"
            ) {
              nextSegments[recordKey] = {
                ...seg,
                translatedText,
                taggedTranslatedText,
                revision:
                  data.segmentRevisions?.[edit.segmentId] ?? seg.revision ?? 0,
                review: {
                  status: "translated",
                  reviewerId: seg.review?.reviewerId,
                  reviewerName: seg.review?.reviewerName,
                  commentCount: seg.review?.commentCount ?? 0,
                  unresolvedCommentCount:
                    seg.review?.unresolvedCommentCount ?? 0,
                  locked: seg.review?.locked ?? false,
                  lockedBy: seg.review?.lockedBy,
                  lockedByName: seg.review?.lockedByName,
                },
              }
              changed = true
            }
          }
          if (!changed) return old
          return {
            ...old,
            data: { ...old.data, segments: nextSegments },
          }
        }
      )
      variables.edits.forEach((edit) => {
        void queryClient.invalidateQueries({
          queryKey: [
            TranslationQueryKeysEnum.FetchCatSegmentEditHistory,
            variables.jobId,
            edit.segmentId,
          ],
        })
      })
      if (onSuccess) {
        onSuccess(data)
      }
    },
    onError,
    onSettled,
  })
}

export const useEditMappingsOfGlossaryMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: editMappingsOfGlossary,
    onSuccess: (data) => {
      if (onSuccess) {
        onSuccess(data)
      }
    },
    onError,
    onSettled,
  })
}

export const useDeleteFileMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: deleteFile,
    onSuccess: (data, variables) => {
      clearReviewResourceCompletions("cat-review", variables.jobId)
      clearCompletionInboxResource("cat-review", variables.jobId)
      if (onSuccess) {
        onSuccess(data)
      }
    },
    onError,
    onSettled,
  })
}
export const useTranslateFileMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: translateFile,
    onSuccess: (data) => {
      if (onSuccess) {
        onSuccess(data)
      }
    },
    onError,
    onSettled,
  })
}

export const useTranslateTextMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: translateText,
    onSuccess: (data) => {
      if (onSuccess) {
        onSuccess(data)
      }
    },
    onError,
    onSettled,
  })
}

export const useCreateGlossaryMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: createGlossary,
    onSuccess: (data) => {
      if (onSuccess) {
        onSuccess(data)
      }
    },
    onError,
    onSettled,
  })
}

export const useDeleteGlossaryMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: deleteGlossary,
    onSuccess: (data) => {
      if (onSuccess) {
        onSuccess(data)
      }
    },
    onError,
    onSettled,
  })
}

export const useSendFeedbackMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: sendFeedback,
    onSuccess: (data) => {
      if (onSuccess) {
        onSuccess(data)
      }
    },
    onError,
    onSettled,
  })
}

export const useUpdateSmartGlossaryMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: updateSmartGlossary,
    onSuccess: (data) => {
      if (onSuccess) {
        onSuccess(data)
      }
    },
    onError,
    onSettled,
  })
}

export const useMergeSmartGlossaryIntoExistingGlossaryMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: mergeSmartGlossaryIntoExistingGlossary,
    onSuccess: (data) => {
      if (onSuccess) {
        onSuccess(data)
      }
    },
    onError,
    onSettled,
  })
}

export const usePredictCostMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: predictCost,
    onSuccess: (data) => {
      if (onSuccess) {
        onSuccess(data)
      }
    },
    onError,
    onSettled,
  })
}

export const useStartMediaTranslationMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: startMediaTranslation,
    onSuccess: (data) => {
      if (onSuccess) {
        onSuccess(data)
      }
    },
    onError,
    onSettled,
  })
}

export const useUpdateEditorTitleMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: updateEditorTitle,
    onSuccess: (data) => {
      if (onSuccess) {
        onSuccess(data)
      }
    },
    onError,
    onSettled,
  })
}
